import { CommonModule, NgFor, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component, Inject, Input, NgZone, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { IconReportComponent } from "@src/app/components/svg/icon-report/icon-report.component";
import { CookieService } from "ngx-cookie-service";
import { environment } from "../../../../environments/environment";
import { IconCommentsComponent } from "../../../components/svg/icon-comments/icon-comments.component";
import { IconTopArrowComponent } from "../../../components/svg/icon-top-arrow/icon-top-arrow.component";
import { SendCommentTextareaComponent } from "../send-comment-textarea/send-comment-textarea.component";
import { IconDownArrowComponent } from "@src/app/components/svg/icon-down-arrow/icon-down-arrow.component";
import { ComentariosService } from "@src/app/servicios/comentarios/comentarios.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-comment",
  imports: [MatIconModule, CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, SendCommentTextareaComponent, NgIf, NgFor, IconCommentsComponent, IconReportComponent, IconTopArrowComponent, IconDownArrowComponent],
  providers: [ComentariosService],
  templateUrl: "./comment.component.html",
  styleUrl: "./comment.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentComponent implements OnInit {
  @Input() idVideo;
  @Input() comment;
  @Input() showFecha: boolean;
  @Input() showNombre: boolean;
  @Input() showMore: boolean = false;

  // Variables principales
  nameToShow: string = "";
  comentarioForm: FormGroup;
  showTextarea: boolean = false;
  shake = false;
  placeholder: any;
  comentariosAnonimos: boolean;
  disabledReport = false;
  login = false;
  userId;
  reportedText: string;
  isLiked = false;
  isDisliked = false;

  constructor(
    private ngZone: NgZone,
    @Inject(FormBuilder) private formBuilder: FormBuilder,
    private comentariosService: ComentariosService,
    private cookieService: CookieService,
    public dialog: MatDialog,
  ) {
    this.comentarioForm = this.formBuilder.group({
      comentario: [
        "",
        {
          validators: [Validators.required, Validators.minLength(3), Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)],
        },
      ],
    });
  }

  /**
   * Inicializa el componente.
   * Configura el estado inicial de los likes, login y nombre a mostrar.
   */
  ngOnInit(): void {
    this.disabledReport = !!+this.comment.reportado;
    if (this.comment?.tipoVoto) {
      if (this.comment?.tipoVoto === "like") {
        this.isLiked = true;
      }
      if (this.comment?.tipoVoto === "dislike") {
        this.isDisliked = true;
      }
    }
    if (this.cookieService.get("userLogin" + environment.name)) {
      this.login = true;
      this.userId = JSON.parse(this.cookieService.get("userLogin" + environment.name)).id;
    }
    if (this.comment.nombre) {
      this.nameToShow = this.comment.nombre.toLowerCase();
      if (this.comment.apellidos) {
        this.nameToShow = this.nameToShow + " " + this.comment.apellidos.toLowerCase();
      }
    }
  }

  /**
   * Devuelve una cadena representando la diferencia de tiempo entre la fecha de entrada y ahora.
   * @param inputDateTime Fecha de entrada.
   * @returns Cadena formateada con la diferencia de tiempo.
   */
  getDisplayDate(inputDateTime: string): string {
    const date = new Date(inputDateTime);
    const now = Date.now();
    const diffMs = now - date.getTime();

    const oneMinute = 60 * 1000;
    const oneHour = 60 * oneMinute;
    const oneDay = 24 * oneHour;

    if (diffMs < 0) {
      return `${date.toLocaleDateString()} - ${date.toLocaleTimeString()}`;
    } else if (diffMs < oneMinute) {
      return "hace un momento";
    } else if (diffMs < oneHour) {
      return `hace ${Math.floor(diffMs / oneMinute)} minutos`;
    } else if (diffMs < oneDay) {
      return `hace ${Math.floor(diffMs / oneHour)} horas`;
    } else {
      return `${date.toLocaleDateString()} - ${date.toLocaleTimeString()}`;
    }
  }

  /**
   * Muestra más contenido.
   */
  onShowMore(): void {
    this.showMore = true;
  }

  /**
   * Gestiona la acción de "like" en el comentario.
   * Si el usuario no está logueado, abre el diálogo de login.
   */
  onLike(): void {
    if (!this.login) {
      this.openLoginDialog();
      return;
    }
    const previousRate = { like: this.isLiked, dislike: this.isDisliked, counter: this.comment.score };
    if (this.isLiked) {
      this.isLiked = false;
      this.comment.score--;
    } else {
      if (this.isDisliked) {
        this.isDisliked = false;
        this.isLiked = true;
        this.comment.score = Number(this.comment.score) + 2;
      } else {
        this.isLiked = true;
        this.comment.score++;
      }
    }
    firstValueFrom(this.comentariosService.rateComment(this.comment.id, this.userId, "like"))
      .then()
      .catch(() => {
        this.isLiked = previousRate.like;
        this.isDisliked = previousRate.dislike;
        this.comment.score = previousRate.counter;
      });
  }

  /**
   * Gestiona la acción de "dislike" en el comentario.
   * Si el usuario no está logueado, abre el diálogo de login.
   */
  onDislike(): void {
    if (!this.login) {
      this.openLoginDialog();
      return;
    }
    const previousRate = { like: this.isLiked, dislike: this.isDisliked, counter: this.comment.score };
    if (this.isDisliked) {
      this.isDisliked = false;
      this.comment.score++;
    } else {
      if (this.isLiked) {
        this.isLiked = false;
        this.isDisliked = true;
        this.comment.score = Number(this.comment.score) - 2;
      } else {
        this.isDisliked = true;
        this.comment.score--;
      }
    }
    firstValueFrom(this.comentariosService.rateComment(this.comment.id, this.userId, "dislike"))
      .then()
      .catch(() => {
        this.isLiked = previousRate.like;
        this.isDisliked = previousRate.dislike;
        this.comment.score = previousRate.counter;
      });
  }

  /**
   * Alterna la visualización del área de texto para comentar.
   */
  onShowTextarea(): void {
    this.showTextarea = !this.showTextarea;
  }

  /**
   * Reporta un comentario.
   * Si el usuario está logueado, envía el reporte; de lo contrario, abre el diálogo de login.
   */
  onReport(): void {
    if (this.login) {
      firstValueFrom(this.comentariosService.reportComment(this.comment.id, this.userId))
        .then(() => {
          this.reportedText = "Reportado";
          this.disabledReport = true;
        })
        .catch((response) => {
          this.reportedText = response.error.message;
          if (response.status === 400) {
            this.disabledReport = true;
          }
        });
    } else {
      this.openLoginDialog();
    }
  }

  /**
   * Abre el diálogo de login (carga el componente de forma dinámica).
   */
  openLoginDialog(): void {
    import("../../modal-login/modal-login.component").then(({ ModalLoginComponent }) => {
      this.dialog.open(ModalLoginComponent, {
        panelClass: "modal-login",
        maxWidth: "85%",
        minWidth: "50%",
      });
    });
  }

  /**
   * Envía un comentario hijo.
   * @param event Nuevo comentario hijo.
   */
  onSendComment(event): void {
    const newChildComment = event;
    event.profundidad = this.comment.profundidad + 1;
    if (this.comment.children) {
      this.comment.children.push(newChildComment);
    } else {
      this.comment.children = [newChildComment];
    }
  }
}
